import React, { useEffect, useState } from 'react';
import { uploads as uploadsApi } from '@/utils/api';
import { UploadCloud, Save, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function UploadsPanel() {
  const [cfg, setCfg] = useState({ enabled: false, account_id: '', bucket: '', public_base_url: '', access_key_set: false, secret_key_set: false });
  const [form, setForm] = useState({ account_id: '', access_key: '', secret_key: '', bucket: '', public_base_url: '', enabled: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const reload = () => uploadsApi.getConfig().then(r => {
    setCfg(r.data);
    setForm({
      account_id: r.data.account_id || '',
      access_key: r.data.access_key_set ? '••••••••' : '',
      secret_key: r.data.secret_key_set ? '••••••••' : '',
      bucket: r.data.bucket || '',
      public_base_url: r.data.public_base_url || '',
      enabled: r.data.enabled,
    });
  });
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (body.access_key === '••••••••') delete body.access_key;
      if (body.secret_key === '••••••••') delete body.secret_key;
      await uploadsApi.setConfig(body);
      toast.success('Saved');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try { const r = await uploadsApi.test(); toast.success(`Connected to bucket: ${r.data.bucket}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-5" data-testid="uploads-panel">
      <div>
        <h2 className="text-lg font-black mb-1 flex items-center gap-2" style={{ color: 'var(--ink)' }}>
          <UploadCloud size={18} /> Cloudflare R2 Uploads
        </h2>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>
          Connect your R2 bucket so onboarding wizards, documents, and video demos can be uploaded inline (instead of emailed).
        </p>
      </div>

      <div className="card-base p-4">
        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--ink)' }}>
            <input type="checkbox" checked={form.enabled} onChange={e => setForm({...form, enabled: e.target.checked})} data-testid="r2-enabled" />
            Enable inline uploads
          </label>
          {cfg.enabled && cfg.access_key_set && cfg.secret_key_set && cfg.account_id && cfg.bucket && cfg.public_base_url ? (
            <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>Active</span>
          ) : cfg.enabled ? (
            <span className="badge-status" style={{ background: '#D9A44115', color: '#D9A441' }}>Incomplete</span>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: 'var(--muted)' }}>Account ID</label>
            <input className="input-agency text-sm font-mono" value={form.account_id} onChange={e => setForm({...form, account_id: e.target.value})} data-testid="r2-account-id" placeholder="abc123..." />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: 'var(--muted)' }}>Bucket name</label>
            <input className="input-agency text-sm" value={form.bucket} onChange={e => setForm({...form, bucket: e.target.value})} data-testid="r2-bucket" placeholder="agency-uploads" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: 'var(--muted)' }}>Access Key ID</label>
            <input className="input-agency text-sm font-mono" value={form.access_key} onChange={e => setForm({...form, access_key: e.target.value})} data-testid="r2-access-key" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: 'var(--muted)' }}>Secret Access Key</label>
            <input className="input-agency text-sm font-mono" type="password" value={form.secret_key} onChange={e => setForm({...form, secret_key: e.target.value})} data-testid="r2-secret-key" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: 'var(--muted)' }}>Public Base URL</label>
            <input className="input-agency text-sm font-mono" type="url" value={form.public_base_url} onChange={e => setForm({...form, public_base_url: e.target.value})} placeholder="https://cdn.youragency.com" data-testid="r2-public-url" />
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Your R2 custom domain or r2.dev public URL — files will be served from here.</p>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button className="btn-primary text-sm flex items-center gap-1.5" onClick={save} disabled={saving} data-testid="save-r2">
            <Save size={13} /> {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="text-sm px-3 py-2 rounded-btn font-semibold" style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--border)' }} onClick={test} disabled={testing || !cfg.enabled} data-testid="test-r2">
            {testing ? 'Testing…' : 'Test connection'}
          </button>
        </div>
      </div>

      <div className="card-base p-4" style={{ background: '#E6F1F4' }}>
        <p className="text-xs font-bold uppercase mb-2" style={{ color: '#006080' }}>How to set up R2</p>
        <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: 'var(--ink)' }}>
          <li>Go to Cloudflare Dashboard → R2 → Create bucket</li>
          <li>Enable public access (r2.dev subdomain or attach custom domain)</li>
          <li>R2 → Manage API Tokens → Create Token (Object Read &amp; Write)</li>
          <li>Paste Account ID, Access Key, Secret Key, Bucket, and Public URL above</li>
          <li>Add your app domain to the bucket's CORS policy: allowed methods PUT, GET, POST, DELETE, HEAD</li>
          <li>Toggle "Enable" ✓ and Save — test connection should succeed</li>
        </ol>
        <a href="https://developers.cloudflare.com/r2/buckets/cors/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs mt-2 font-semibold" style={{ color: '#006080' }}>
          <ExternalLink size={11} /> R2 CORS docs
        </a>
      </div>
    </div>
  );
}
